import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  // ATTR_DEPLOYMENT_ENVIRONMENT,
} from '@opentelemetry/semantic-conventions';
import { environment } from '../constants';

/**
 * Initialize frontend OpenTelemetry tracing with backend proxy exporter.
 * This sends frontend traces to the backend proxy endpoint to avoid CORS,
 * and propagates trace context to backend API calls.
 */
export function initFrontendTracing() {
  // Get the API base URL for the current environment
  let apiBaseUrl: string;
  if (environment === 'local') {
    apiBaseUrl = 'http://localhost:4201';
  } else if (environment === 'production') {
    apiBaseUrl = 'https://api.viewmetrics.com';
  } else {
    apiBaseUrl = 'https://staging-api.viewmetrics.com';
  }

  // 1️⃣ Create the provider with service metadata
  const provider = new WebTracerProvider({
    resource: new Resource({
      [ATTR_SERVICE_NAME]: 'vm-frontend',
      [ATTR_SERVICE_VERSION]: import.meta.env.VITE_SERVICE_VERSION || '1.0.0',
      // [ATTR_DEPLOYMENT_ENVIRONMENT]: import.meta.env.NODE_ENV || 'development',
    }),
  });

  // 2️⃣ Create OTLP exporter that sends to our backend proxy
  const exporter = new OTLPTraceExporter({
    url: `${apiBaseUrl}/otel-proxy`,
    headers: {},
  });

  // 3️⃣ Add batch processor with exporter
  provider.addSpanProcessor(new BatchSpanProcessor(exporter));

  // 4️⃣ Register the provider
  provider.register({
    contextManager: new ZoneContextManager(),
  });

  // 5️⃣ Register instrumentations - ONLY trace backend API calls
  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [
          /localhost:4201/,
          /staging-api\.viewmetrics\.com/,
          /api\.viewmetrics\.com/,
        ],
        clearTimingResources: true,
        ignoreUrls: [
          // Ignore the otel-proxy endpoint
          url => url.includes('/otel-proxy'),
          // Ignore everything that's NOT our backend
          url => {
            return !(
              url.includes('localhost:4201') ||
              url.includes('staging-api.viewmetrics.com') ||
              url.includes('api.viewmetrics.com')
            );
          },
        ],
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [
          /localhost:4201/,
          /staging-api\.viewmetrics\.com/,
          /api\.viewmetrics\.com/,
        ],
        clearTimingResources: true,
        ignoreUrls: [
          // Ignore the otel-proxy endpoint
          url => url.includes('/otel-proxy'),
          // Ignore everything that's NOT our backend
          url => {
            return !(
              url.includes('localhost:4201') ||
              url.includes('staging-api.viewmetrics.com') ||
              url.includes('api.viewmetrics.com')
            );
          },
        ],
      }),
    ],
  });

  console.log('✅ Frontend tracing initialized with backend proxy exporter');
  console.log(`   Sending traces to: ${apiBaseUrl}/otel-proxy`);
}
